import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, ArrowRight, X, Check } from "lucide-react";

// Phase 6.4: il widget fluttuante è il tier FREE — solo testo, niente voce.
// La voce + glow + waveform vivono nel layer cinematico (PresenceInlineChat in TotalKlickHub).

const EASE = [0.16, 1, 0.3, 1];
const BACKEND = "";
const STORAGE_KEY = "aria_session_id";
const LEAD_KEY = "aria_lead_submitted";
const NUDGE_KEY = "aria_nudge_categories";
const TELEMETRY_KEY = "aria_telemetry_v1";

/**
 * AriaFloatingWidget — Intercom-style global chat widget.
 * • Collapsed: round button bottom-right (60×60).
 * • Expanded: 440×640 card (mobile: near-full-screen) — testo ingrandito.
 * • Form lead a 3 trigger contestuali: natural / no_match / newsletter.
 * • Pulsante chiusura sul form: l'utente può riaprilo se Aria lo ripropone.
 * • Ambient awareness: nudge contestuale calmo quando l'utente esplora un'area.
 * • Cross-sell `[ARIA_FOR_SITE]`: card "Vuoi Aria anche sul tuo sito?".
 * • Checklist rendering: linee che iniziano con `▢ ` diventano voci checkbox.
 */
export default function AriaFloatingWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "Ciao, sono Aria — l'assistente di TotalKlick. Raccontami brevemente: di cosa ti occupi e cosa stai cercando?",
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(() => {
        try { return window.localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
    });
    const [cta, setCta] = useState(null);
    const [hasUnread, setHasUnread] = useState(false);
    const [leadAsked, setLeadAsked] = useState(false);
    const [leadReason, setLeadReason] = useState(null);   // "natural" | "no_match" | "newsletter"
    const [leadSubmitted, setLeadSubmitted] = useState(() => {
        try { return window.localStorage.getItem(LEAD_KEY) === "1"; } catch (e) { return false; }
    });
    const [ariaForSite, setAriaForSite] = useState(false);
    // Ambient nudge
    const [nudge, setNudge] = useState(null);
    const listRef = useRef(null);

    // === Persistenza sessione ===
    useEffect(() => {
        if (sessionId) {
            try { window.localStorage.setItem(STORAGE_KEY, sessionId); } catch (e) { /* ignore */ }
        }
    }, [sessionId]);

    // === v3.6.5 · MongoDB Chat Persistence · Sync cross-device ===
    // Al mount (o quando sessionId cambia), scarica lo storico dal backend
    // così Aria riprende esattamente dove eri su qualsiasi dispositivo.
    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(
                    `${BACKEND}/api/aria/chat/history?session_id=${encodeURIComponent(sessionId)}&limit=200`,
                    { headers: { "Accept": "application/json" } },
                );
                if (!r.ok) return;
                const data = await r.json();
                if (cancelled) return;
                if (Array.isArray(data.messages) && data.messages.length > 0) {
                    setMessages(data.messages.map((m) => ({
                        role: m.role, content: m.content,
                    })));
                }
            } catch (e) { /* fallback: keep locale */ }
        })();
        return () => { cancelled = true; };
    }, [sessionId]);

    // Helper: persiste un messaggio sul backend (fire-and-forget).
    const persistMessage = (sid, role, content) => {
        if (!sid || !role || !content) return;
        try {
            fetch(`${BACKEND}/api/aria/chat/history/append`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sid, role, content }),
                keepalive: true,
            }).catch(() => { /* silent */ });
        } catch (e) { /* silent */ }
    };

    // === Presence Layer hide: se la pagina monta un layer di presenza Aria
    // (es. /, /aria), il widget fluttuante deve sparire del tutto per evitare
    // sovrapposizioni e "due Aria" visibili insieme.
    const [presenceLayerActive, setPresenceLayerActive] = useState(() => {
        if (typeof window === "undefined") return false;
        return !!window.__ariaHasPresenceLayer;
    });
    useEffect(() => {
        const onMounted = () => setPresenceLayerActive(true);
        const onUnmounted = () => setPresenceLayerActive(false);
        window.addEventListener("aria-presence-mounted", onMounted);
        window.addEventListener("aria-presence-unmounted", onUnmounted);
        return () => {
            window.removeEventListener("aria-presence-mounted", onMounted);
            window.removeEventListener("aria-presence-unmounted", onUnmounted);
        };
    }, []);

    // === Open from external CTAs ===
    useEffect(() => {
        const handler = () => {
            // Skip se la pagina corrente ha il LAYER CINEMATIC attivo (tier full).
            // Il widget fluttuante è il tier free di supporto e non deve doppiare il layer.
            if (typeof window !== "undefined" && window.__ariaHasPresenceLayer) {
                return;
            }
            setOpen(true);
            setHasUnread(false);
            setNudge(null);
        };
        window.addEventListener("open-aria", handler);
        return () => window.removeEventListener("open-aria", handler);
    }, []);

    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, loading, open]);

    // === AMBIENT TELEMETRY (calma & umana) ===
    // Tracking passivo del comportamento utente per nudge contestuale.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const path = window.location.pathname;
        const start = Date.now();
        let maxScroll = 0;

        const onScroll = () => {
            const sc = window.scrollY;
            const docH = (document.documentElement.scrollHeight - window.innerHeight) || 1;
            const pct = Math.min(100, Math.round((sc / docH) * 100));
            if (pct > maxScroll) maxScroll = pct;
        };
        window.addEventListener("scroll", onScroll, { passive: true });

        const flush = () => {
            try {
                const raw = window.localStorage.getItem(TELEMETRY_KEY);
                const tel = raw ? JSON.parse(raw) : { paths: [] };
                const seconds = Math.round((Date.now() - start) / 1000);
                if (seconds < 4) return; // ignora rimbalzi veloci
                tel.paths = (tel.paths || []).filter((p) => p && p.at && (Date.now() - p.at < 30 * 60 * 1000));
                tel.paths.push({ path, seconds_on_page: seconds, scroll_depth_pct: maxScroll, at: Date.now() });
                tel.paths = tel.paths.slice(-30);
                window.localStorage.setItem(TELEMETRY_KEY, JSON.stringify(tel));
            } catch (e) { /* ignore */ }
        };

        const beforeUnload = () => flush();
        window.addEventListener("beforeunload", beforeUnload);

        // Tick periodico per chiamata ambient — solo se chat chiusa, nessun nudge attivo, lead non inviato
        const tick = setInterval(async () => {
            if (open || nudge || leadSubmitted) return;
            try {
                const raw = window.localStorage.getItem(TELEMETRY_KEY);
                const tel = raw ? JSON.parse(raw) : { paths: [] };
                const seconds_now = Math.round((Date.now() - start) / 1000);
                const recent_paths = [
                    ...(tel.paths || []).slice(-10),
                    { path, seconds_on_page: seconds_now, scroll_depth_pct: maxScroll },
                ];
                const nudgedRaw = window.localStorage.getItem(NUDGE_KEY);
                const already = nudgedRaw ? JSON.parse(nudgedRaw) : [];
                const res = await fetch(`${BACKEND}/api/hub/ambient-check`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        session_id: sessionId,
                        current_path: path,
                        recent_paths,
                        already_nudged_categories: already,
                    }),
                });
                if (!res.ok) return;
                const data = await res.json();
                if (data?.should_show && data.category && !already.includes(data.category)) {
                    setNudge(data);
                }
            } catch (e) { /* ignore */ }
        }, 15000); // check ogni 15s, ma il backend richiede ≥25-35s di permanenza

        return () => {
            flush();
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("beforeunload", beforeUnload);
            clearInterval(tick);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, leadSubmitted, nudge]);

    const dismissNudge = () => {
        if (nudge?.category) {
            try {
                const raw = window.localStorage.getItem(NUDGE_KEY);
                const arr = raw ? JSON.parse(raw) : [];
                if (!arr.includes(nudge.category)) arr.push(nudge.category);
                window.localStorage.setItem(NUDGE_KEY, JSON.stringify(arr.slice(-20)));
            } catch (e) { /* ignore */ }
        }
        setNudge(null);
    };

    const openFromNudge = () => {
        if (nudge?.message) {
            setMessages((m) => [...m, { role: "user", content: nudge.message }]);
        }
        dismissNudge();
        setOpen(true);
        setHasUnread(false);
    };

    // Hide on internal app routes and embed
    const hidden = (() => {
        const p = (typeof window !== "undefined" ? window.location.pathname : "") || "";
        return p.startsWith("/login") || p.startsWith("/register") || p.startsWith("/app/") || p.startsWith("/aria-embed");
    })();
    if (hidden) return null;

    const send = async (text) => {
        const userText = (text || input).trim();
        if (!userText || loading) return;
        const nextMessages = [...messages, { role: "user", content: userText }];
        setMessages(nextMessages);
        setInput("");
        setLoading(true);
        // v3.6.5 · persisti user message subito (session_id conosciuto se già settato)
        if (sessionId) persistMessage(sessionId, "user", userText);
        try {
            const res = await fetch(`${BACKEND}/api/hub/ai-guide`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    session_id: sessionId,
                    message: userText,
                    history: messages.slice(-14),
                    path: typeof window !== "undefined" ? window.location.pathname : null,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || "AI offline");
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
            // v3.6.5 · persisti user (se il session_id era nuovo) + assistant reply
            if (!sessionId && data.session_id) {
                persistMessage(data.session_id, "user", userText);
            }
            persistMessage(data.session_id, "assistant", data.reply);
            if (data.cta) setCta(data.cta);
            if (!open) setHasUnread(true);

            // === NUOVO TRIGGER FORM: solo contestuale dal backend ===
            if (!leadSubmitted && data.wants_lead && data.lead_reason) {
                setLeadAsked(true);
                setLeadReason(data.lead_reason);
            }
            if (data.aria_for_site) setAriaForSite(true);
        } catch (e) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content: "Mi spiace, c'è un piccolo intoppo. Riprova tra un istante.",
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const quickReplies = ["Ho una tipografia", "Faccio agenzia/ads", "Ho un'attività locale", "Mi serve materiale stampato"];

    // Hide the entire floating widget when a Presence Layer is mounted (e.g. on Home or /aria),
    // so Aria has a SINGLE point of presence per page.
    if (presenceLayerActive) return null;

    return (
        <>
            {/* === Ambient nudge bubble (sopra il launcher) === */}
            <AnimatePresence>
                {!open && nudge && nudge.should_show && (
                    <motion.div
                        key="nudge"
                        initial={{ opacity: 0, y: 16, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.96 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.4 }}
                        className="fixed z-[60]"
                        style={{
                            bottom: 92,
                            right: 20,
                            maxWidth: "min(320px, calc(100vw - 40px))",
                            background: "rgba(10,12,22,0.96)",
                            border: "1px solid rgba(79,139,255,0.35)",
                            borderRadius: 18,
                            padding: "14px 16px",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                            boxShadow: "0 22px 60px -12px rgba(79,139,255,0.45), 0 0 0 1px rgba(255,255,255,0.04)",
                            fontFamily: "Outfit, Geist, sans-serif",
                        }}
                        data-testid="aria-nudge"
                    >
                        <div className="flex items-start gap-2">
                            <Sparkles size={14} className="text-[#9EC3FF] flex-none mt-1" />
                            <div className="flex-1 text-white/90 text-[14px] leading-snug">
                                {nudge.message}
                            </div>
                            <button
                                type="button"
                                onClick={dismissNudge}
                                className="text-white/45 hover:text-white flex-none"
                                aria-label="Chiudi suggerimento"
                                data-testid="aria-nudge-close"
                            >
                                <X size={14} />
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={openFromNudge}
                            className="mt-2.5 inline-flex items-center gap-1.5 text-[12px] text-[#9EC3FF] hover:text-white transition"
                            data-testid="aria-nudge-open"
                        >
                            Ne parliamo insieme <ArrowRight size={11} />
                        </button>
                        {/* "Tail" che punta al launcher */}
                        <div
                            className="absolute"
                            style={{
                                right: 28, bottom: -7, width: 14, height: 14,
                                transform: "rotate(45deg)",
                                background: "rgba(10,12,22,0.96)",
                                borderRight: "1px solid rgba(79,139,255,0.35)",
                                borderBottom: "1px solid rgba(79,139,255,0.35)",
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating launcher button */}
            <AnimatePresence>
                {!open && (
                    <motion.button
                        key="launcher"
                        initial={{ opacity: 0, scale: 0.6, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.6, y: 20 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        onClick={() => {
                            setOpen(true);
                            setHasUnread(false);
                            setNudge(null);
                        }}
                        className="fixed z-[60] flex items-center justify-center group"
                        style={{
                            bottom: 20,
                            right: 20,
                            width: 60,
                            height: 60,
                            borderRadius: 999,
                            background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                            boxShadow: "0 14px 38px -8px rgba(79,139,255,0.6), 0 0 0 1px rgba(255,255,255,0.08)",
                            border: "none",
                            cursor: "pointer",
                        }}
                        aria-label="Apri Aria"
                        data-testid="aria-launcher"
                    >
                        <motion.span
                            animate={{ scale: [1, 1.18, 1] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute inset-0 rounded-full"
                            style={{ background: "radial-gradient(circle, rgba(79,139,255,0.5) 0%, rgba(79,139,255,0) 70%)", filter: "blur(8px)" }}
                        />
                        <Sparkles size={24} className="text-white relative z-10" />
                        {hasUnread && (
                            <span
                                className="absolute top-1 right-1 w-3 h-3 rounded-full ring-2"
                                style={{ background: "#FF2D7B", boxShadow: "0 0 12px #FF2D7B", borderColor: "#0a0c16" }}
                            />
                        )}
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Expanded panel — più grande, testo più leggibile */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="panel"
                        initial={{ opacity: 0, scale: 0.95, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 24 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="fixed z-[60] flex flex-col"
                        style={{
                            bottom: 20,
                            right: 20,
                            width: "min(440px, calc(100vw - 32px))",
                            height: "min(640px, calc(100vh - 40px))",
                            borderRadius: 24,
                            background: "rgba(10,12,22,0.94)",
                            border: "1px solid rgba(79,139,255,0.28)",
                            backdropFilter: "blur(28px)",
                            WebkitBackdropFilter: "blur(28px)",
                            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
                            overflow: "hidden",
                        }}
                        data-testid="aria-panel"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/5 flex-shrink-0">
                            <div className="flex items-center gap-3 min-w-0">
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{
                                        background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                        boxShadow: "0 0 18px -2px rgba(79,139,255,0.6)",
                                    }}
                                >
                                    <Sparkles size={16} className="text-white" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-white text-[15px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                        Aria · AI Guide
                                    </div>
                                    <div className="text-[11px] text-emerald-400 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                        Online
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white/55 hover:text-white hover:bg-white/8 transition"
                                    aria-label="Chiudi"
                                    data-testid="aria-close"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div
                            ref={listRef}
                            className="flex-1 px-5 py-4 space-y-3 overflow-y-auto"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                            {messages.map((m, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, ease: EASE }}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className="max-w-[88%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed"
                                        style={{
                                            background:
                                                m.role === "user"
                                                    ? "linear-gradient(135deg, rgba(79,139,255,0.22) 0%, rgba(43,90,255,0.18) 100%)"
                                                    : "rgba(255,255,255,0.05)",
                                            color: "rgba(255,255,255,0.94)",
                                            border: m.role === "user" ? "1px solid rgba(79,139,255,0.30)" : "1px solid rgba(255,255,255,0.06)",
                                        }}
                                    >
                                        <RenderMessage content={m.content} />
                                    </div>
                                </motion.div>
                            ))}
                            {loading && (
                                <div className="flex justify-start">
                                    <div
                                        className="px-3.5 py-2.5 rounded-2xl"
                                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.06)" }}
                                    >
                                        <span className="inline-flex gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "120ms" }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "240ms" }} />
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* CTA card when AI proposes a product */}
                            {cta && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, ease: EASE }}
                                >
                                    <a
                                        href={cta.url}
                                        target={cta.url.startsWith("http") ? "_blank" : "_self"}
                                        rel={cta.url.startsWith("http") ? "noopener noreferrer" : ""}
                                        className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl group transition hover:scale-[1.01]"
                                        style={{
                                            background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                            fontFamily: "Geist, sans-serif",
                                            boxShadow: "0 14px 40px -12px rgba(79,139,255,0.55)",
                                        }}
                                        data-testid="aria-cta"
                                    >
                                        <span className="text-white text-[14px] font-medium">{cta.label}</span>
                                        <ArrowRight size={16} className="text-white transition group-hover:translate-x-0.5" />
                                    </a>
                                </motion.div>
                            )}

                            {/* === Aria for your site (cross-sell ecosistema) === */}
                            {ariaForSite && (
                                <AriaForSiteCard onDismiss={() => setAriaForSite(false)} />
                            )}
                        </div>

                        {/* === Lead form OVERLAY SHEET — sempre visibile, indipendente
                              dalla lunghezza della conversazione (fix UX: prima era inline
                              dopo i messaggi e finiva oltre il bordo viewport).
                              Si sovrappone al body della chat lasciando visibile l'header,
                              così l'utente può chiudere il widget. Sostituisce anche l'input
                              bar in basso finché il form è aperto. === */}
                        <AnimatePresence>
                            {leadAsked && !leadSubmitted && (
                                <motion.div
                                    key="lead-overlay"
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 16 }}
                                    transition={{ duration: 0.35, ease: EASE }}
                                    className="absolute left-0 right-0 bottom-0 overflow-y-auto"
                                    style={{
                                        top: 70,  // sotto l'header
                                        background: "linear-gradient(180deg, rgba(10,12,22,0.92) 0%, rgba(10,12,22,0.98) 14%, rgba(10,12,22,0.99) 100%)",
                                        backdropFilter: "blur(18px)",
                                        WebkitBackdropFilter: "blur(18px)",
                                        borderTop: "1px solid rgba(79,139,255,0.32)",
                                        padding: "16px 18px 18px",
                                        zIndex: 5,
                                    }}
                                    data-testid="aria-lead-overlay"
                                >
                                    <AriaLeadForm
                                        reason={leadReason}
                                        onClose={() => setLeadAsked(false)}
                                        onSuccess={() => {
                                            setLeadSubmitted(true);
                                            try { window.localStorage.setItem(LEAD_KEY, "1"); } catch (e) { /* ignore */ }
                                            setLeadAsked(false);
                                            setMessages((m) => [...m, { role: "assistant", content: leadReason === "newsletter" ? "Perfetto, ti scrivo solo le cose davvero utili. Continuiamo?" : "Perfetto, ti ricontatto a breve. Nel frattempo continuiamo: c'è altro che vuoi sapere?" }]);
                                        }}
                                        conversation={messages}
                                        sessionId={sessionId}
                                        ctaProduct={cta?.product}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Quick replies + Input — nascosti quando il lead form overlay è aperto */}
                        {!(leadAsked && !leadSubmitted) && (
                            <>
                                {/* Quick replies (visible only at start) */}
                                {messages.length === 1 && !loading && (
                                    <div className="px-4 py-2 flex flex-wrap gap-2 flex-shrink-0">
                                        {quickReplies.map((q) => (
                                            <button
                                                key={q}
                                                onClick={() => send(q)}
                                                className="text-[12px] px-3 py-1.5 rounded-full border border-white/15 text-white/75 hover:bg-white/5 hover:text-white transition"
                                                style={{ fontFamily: "Geist, sans-serif" }}
                                                data-testid={`aria-quick-${q.replace(/\s+/g, "-").toLowerCase()}`}
                                            >
                                                {q}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Input */}
                                <form
                                    onSubmit={(e) => { e.preventDefault(); send(); }}
                                    className="flex items-center gap-2 px-4 py-3.5 border-t border-white/5 flex-shrink-0"
                                >
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder="Scrivi un messaggio..."
                                        className="flex-1 bg-transparent text-white text-[15px] placeholder:text-white/35 focus:outline-none px-2"
                                        style={{ fontFamily: "Outfit, sans-serif" }}
                                        disabled={loading}
                                        data-testid="aria-input"
                                        autoFocus
                                    />
                                    <button
                                        type="submit"
                                        disabled={!input.trim() || loading}
                                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition disabled:opacity-40"
                                        style={{
                                            background: input.trim() ? "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)" : "rgba(255,255,255,0.06)",
                                        }}
                                        data-testid="aria-send"
                                        aria-label="Invia"
                                    >
                                        <Send size={15} className="text-white" />
                                    </button>
                                </form>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

/**
 * RenderMessage — render leggero: rispetta `\n` e converte le righe
 * che iniziano con `▢ ` in voci checkbox stilizzate.
 */
function RenderMessage({ content }) {
    if (!content) return null;
    const lines = String(content).split(/\r?\n/);
    const out = [];
    let listBuf = [];
    const flushList = (key) => {
        if (!listBuf.length) return;
        out.push(
            <ul key={`l-${key}`} className="my-1.5 space-y-1.5">
                {listBuf.map((t, i) => (
                    <li key={i} className="flex items-start gap-2" data-testid="aria-checklist-item">
                        <span className="mt-1 w-3.5 h-3.5 rounded-sm border border-[#9EC3FF]/60 flex-none" style={{ background: "rgba(79,139,255,0.05)" }} />
                        <span className="flex-1">{t}</span>
                    </li>
                ))}
            </ul>
        );
        listBuf = [];
    };
    lines.forEach((ln, idx) => {
        const m = ln.match(/^\s*▢\s+(.*)$/);
        if (m) {
            listBuf.push(m[1].trim());
        } else {
            flushList(idx);
            if (ln.trim()) out.push(<p key={`p-${idx}`} className="mb-1.5 last:mb-0">{ln}</p>);
        }
    });
    flushList("end");
    return <>{out}</>;
}

/**
 * AriaLeadForm — adattivo per `reason` (natural / no_match / newsletter).
 * Pulsante chiusura X: l'utente può chiuderlo, ma Aria può riproprlo dopo.
 * Esportato anche per riuso nel layer cinematico (PresenceInlineChat).
 */
export function AriaLeadForm({ reason, onClose, onSuccess, conversation, sessionId, ctaProduct }) {
    const [data, setData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        consent: false,
        marketing_consent: false,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const set = (k, v) => setData((p) => ({ ...p, [k]: v }));

    const headerByReason = {
        natural: { label: "contatti per richiamarti", title: "Ti faccio richiamare" },
        no_match: { label: "parla con un esperto umano", title: "Caso specifico — ti passo un umano" },
        newsletter: { label: "resto a portata di mano", title: "Lascia un contatto, ti scriverò solo cose utili" },
    };
    const head = headerByReason[reason] || headerByReason.natural;

    const submit = async (e) => {
        e.preventDefault();
        if (!data.consent) {
            setErr("Per essere ricontattato serve il consenso al trattamento dei dati.");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            // Doppio salvataggio:
            // 1) Pipeline lead pubblica esistente (per pipeline CRM TotalKlick)
            // 2) Aria Hub Tracking (per Learning Dataset + linking alla sessione)
            const lastUser = (conversation || []).filter((m) => m.role === "user").map((m) => m.content).join(" · ").slice(-500);
            const publicLeadPayload = {
                tenant_name: data.last_name || "Lead Aria",
                admin_first_name: data.first_name,
                admin_last_name: data.last_name,
                admin_email: data.email,
                phone_mobile: data.phone,
                main_pain: `[Aria · ${ctaProduct || "general"} · ${reason || "natural"}] ${lastUser}`,
                gdpr_consent: true,
                source: `aria-widget-${reason || "natural"}`,
                session_id: sessionId,
                marketing_consent: data.marketing_consent,
            };
            const res = await fetch(`${""}/api/public/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(publicLeadPayload),
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || "Invio non riuscito");
            }
            // Hub tracking (fire-and-forget — non blocca il flusso utente)
            try {
                await fetch(`${""}/api/hub/submit-lead`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        session_id: sessionId || `aria-${Date.now()}`,
                        first_name: data.first_name,
                        last_name: data.last_name,
                        email: data.email,
                        phone: data.phone,
                        consent: data.consent,
                        marketing_consent: data.marketing_consent,
                        lead_reason: reason || "natural",
                        path: typeof window !== "undefined" ? window.location.pathname : null,
                    }),
                });
            } catch (_e) { /* best-effort */ }
            onSuccess?.();
        } catch (e2) {
            setErr(e2.message || "Errore invio");
        } finally {
            setBusy(false);
        }
    };

    return (
        <motion.form
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            onSubmit={submit}
            className="rounded-2xl p-4 space-y-2 relative"
            style={{
                background: "linear-gradient(135deg, rgba(79,139,255,0.14), rgba(43,90,255,0.07))",
                border: "1px solid rgba(79,139,255,0.38)",
            }}
            data-testid="aria-lead-form"
            data-reason={reason || "natural"}
        >
            <button
                type="button"
                onClick={onClose}
                className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full grid place-items-center text-white/55 hover:text-white hover:bg-white/8 transition"
                aria-label="Chiudi form"
                data-testid="aria-lead-close"
            >
                <X size={13} />
            </button>
            <div className="flex items-center gap-2 mb-1 pr-7">
                <Sparkles size={13} className="text-[#4F8BFF]" />
                <span className="text-[11px] uppercase tracking-[0.18em] text-[#9EC3FF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {head.label}
                </span>
            </div>
            <div className="text-white/95 text-[14px] pb-1" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                {head.title}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
                <input
                    required
                    placeholder="Nome *"
                    value={data.first_name}
                    onChange={(e) => set("first_name", e.target.value)}
                    className="aria-lead-input"
                    data-testid="aria-lead-firstname"
                />
                <input
                    required
                    placeholder="Cognome *"
                    value={data.last_name}
                    onChange={(e) => set("last_name", e.target.value)}
                    className="aria-lead-input"
                    data-testid="aria-lead-lastname"
                />
            </div>
            <input
                required
                type="email"
                placeholder="Email *"
                value={data.email}
                onChange={(e) => set("email", e.target.value)}
                className="aria-lead-input"
                data-testid="aria-lead-email"
            />
            <input
                required={reason !== "newsletter"}
                type="tel"
                placeholder={reason === "newsletter" ? "Telefono (opz.)" : "Telefono *"}
                value={data.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="aria-lead-input"
                data-testid="aria-lead-phone"
            />
            <label className="flex items-start gap-2 cursor-pointer pt-1">
                <input
                    type="checkbox"
                    checked={data.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    className="mt-0.5 w-3.5 h-3.5 cursor-pointer"
                    style={{ accentColor: "#4F8BFF" }}
                    data-testid="aria-lead-consent"
                />
                <span className="text-[11px] text-white/65 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Acconsento al trattamento dei miei dati personali da parte di{" "}
                    <b className="text-white/85">TotalKlick srls</b> per essere ricontattato.
                    {" "}
                    <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-[#9EC3FF] underline">Privacy Policy</a>
                    {" "}— Art. 6.1.a GDPR.
                </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={data.marketing_consent}
                    onChange={(e) => set("marketing_consent", e.target.checked)}
                    className="mt-0.5 w-3.5 h-3.5 cursor-pointer"
                    style={{ accentColor: "#4F8BFF" }}
                    data-testid="aria-lead-marketing-consent"
                />
                <span className="text-[11px] text-white/55 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Opzionale: voglio ricevere offerte e novità su prodotti TotalKlick.
                </span>
            </label>
            {err && <div className="text-rose-300 text-[11px]" data-testid="aria-lead-error">{err}</div>}
            <button
                type="submit"
                disabled={busy}
                className="w-full mt-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full text-white text-[13px] transition disabled:opacity-50"
                style={{
                    background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)",
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: "0.04em",
                    boxShadow: "0 10px 30px -8px rgba(79,139,255,0.55)",
                }}
                data-testid="aria-lead-submit"
            >
                {busy ? "Invio…" : (
                    reason === "newsletter"
                        ? <>Tienimi aggiornato <Check size={12} /></>
                        : <>Fammi richiamare <Check size={12} /></>
                )}
            </button>

            {/* Micro-CTA sotto il form — porta verso TotalKlick per "installare Aria" sul proprio sito */}
            <a
                href="/aria"
                className="block mt-3 px-3 py-2.5 rounded-xl text-center group transition hover:scale-[1.01]"
                style={{
                    background: "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(79,139,255,0.06))",
                    border: "1px dashed rgba(196,181,253,0.4)",
                    fontFamily: "Geist, sans-serif",
                }}
                data-testid="aria-lead-install-cta"
            >
                <div className="text-[#C4B5FD] text-[11px] uppercase tracking-[0.18em] mb-0.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    Vuoi Aria anche tu?
                </div>
                <div className="text-white text-[13px] inline-flex items-center gap-1.5 group-hover:gap-2 transition-all">
                    Installami gratis sulla tua attività <ArrowRight size={12} />
                </div>
            </a>

            <style>{`
                .aria-lead-input { width:100%; padding:9px 11px; border-radius:9px; border:1px solid rgba(255,255,255,0.1); background:rgba(255,255,255,0.04); color:#fff; font-family:'Geist','Outfit',sans-serif; font-size:13.5px; transition:all .15s; }
                .aria-lead-input:focus { outline:none; border-color:rgba(79,139,255,0.55); background:rgba(255,255,255,0.06); }
                .aria-lead-input::placeholder { color:rgba(255,255,255,0.4); }
            `}</style>
        </motion.form>
    );
}

/**
 * AriaForSiteCard — pitch cross-sell ecosistema: "vuoi Aria sul TUO sito?"
 */
function AriaForSiteCard({ onDismiss }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="rounded-2xl p-4 relative"
            style={{
                background: "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(43,90,255,0.08))",
                border: "1px solid rgba(124,58,237,0.35)",
            }}
            data-testid="aria-for-site-card"
        >
            <button
                type="button"
                onClick={onDismiss}
                className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full grid place-items-center text-white/45 hover:text-white"
                aria-label="Chiudi proposta"
                data-testid="aria-for-site-close"
            >
                <X size={12} />
            </button>
            <div className="flex items-center gap-2 mb-1.5 pr-6">
                <Sparkles size={12} className="text-[#C4B5FD]" />
                <span className="text-[10.5px] uppercase tracking-[0.2em] text-[#C4B5FD]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    Aria sul tuo sito
                </span>
            </div>
            <div className="text-white text-[14px] mb-2" style={{ fontFamily: "Geist, sans-serif" }}>
                Vuoi che io curi anche il tuo sito?
            </div>
            <p className="text-white/65 text-[12.5px] mb-3 leading-relaxed">
                Posso ascoltare i tuoi visitatori 24/7, qualificare i lead e darti un database pulito. Gratis, GDPR-ready.
            </p>
            <a
                href="/aria"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[12.5px] transition hover:scale-[1.02]"
                style={{
                    background: "linear-gradient(135deg, #7C3AED, #4F8BFF)",
                    fontFamily: "Geist, sans-serif",
                    boxShadow: "0 10px 30px -10px rgba(124,58,237,0.55)",
                }}
                data-testid="aria-for-site-cta"
            >
                Scopri come <ArrowRight size={12} />
            </a>
        </motion.div>
    );
}
